import {useCallback, useMemo, useRef, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import {t} from 'i18next';
import {useNavigate} from 'react-router';
import ArrDataGridPro from '../../../common/arr-data-grid-pro/ArrDataGridPro';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../../common/arr-menu/ArrMenu';
import ArrModal from '../../../common/arr-modal/ArrModal';
import {ArrToggle} from '../../../common/arr-toggle/ArrToggle';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import CellWrapper from '../../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import FilterTextField from '../../../common/arr-data-grid-pro/filter-components/text-field/FilterTextField';
import {ReactComponent as MoreIcon} from '../../../../assets/images/icon-more.svg';
import {ReactComponent as NoDataIcon} from '../../../../assets/images/no-data-found.svg';
import {
  useGetAllProtocolIntelligenceList,
  useUpdateProtocolActiveState,
  useDeleteAIProtocolIntelligence,
} from '../../../../hooks/ai/review/useReview';
import {
  getProtocolVersionDetails,
  getProtocolVersionHistory,
} from '../../../../api/endpoints/aiReviewApi';
import {
  ProtocolVersion,
  ProtocolPdfDetails,
} from '../../../../api/types/aiReview.type';
import {ToastType} from '../../../../shared/constants';
import {
  buildProtocolListRequest,
  IProtocolColumnsData,
  protocolColumnsData,
  protocolListTableProps,
} from './utils';
import NoProtocols from './NoProtocols';
import {
  PROTOCOL_INTELLIGENCE_ROUTE,
  getNonEmptyText,
  toCapitalized,
} from '../utils';
import {
  createProtocolPdf,
  isProtocolVersionDetails,
} from './protocol-pdf/protocolPdf';
import './ProtocolList.scss';

type ProtocolToastState = {
  isOpen: boolean;
  message: string;
  status: ToastType;
};

const defaultProtocolToastState: ProtocolToastState = {
  isOpen: false,
  message: '',
  status: ToastType.Success,
};

const isConflictError = (error: unknown): boolean => {
  const apiError = error as {status?: number; response?: {status?: number}};
  return apiError?.status === 409 || apiError?.response?.status === 409;
};

export const protocolActionsMoreMenu: CustomMenuItem[] = [
  {
    label: t('ai.review.protocolList.actions.viewEdit'),
    id: 'viewEdit',
  },
  {
    label: t('ai.review.protocolList.actions.download'),
    id: 'download',
  },
  {
    label: t('ai.review.protocolList.actions.deleteProtocol'),
    id: 'deleteProtocol',
  },
];

const cellText = (
  text: string,
  dataTestId: string,
  cellParams: GridRenderCellParams,
  colorClass = 'text-headings'
) => (
  <Box className="cell-items">
    <CellWrapper
      toolTipContent={text ?? ''}
      dataTestId={`tooltip-cell-content-${cellParams.id}`}
      cellParams={cellParams}
    >
      <Typography
        className={`cell-content typo-text body3_size ${colorClass}`}
        data-testid={dataTestId}
      >
        {text?.trim() ? text : '---'}
      </Typography>
    </CellWrapper>
  </Box>
);

const isProtocolActive = (isActive: ProtocolVersion['isActive']) =>
  isActive === 1;

const getStatusColorClass = (status: string) =>
  status.trim().toLowerCase() === 'draft'
    ? 'text-body-subtle'
    : 'text-headings';

const getProtocolNavigationStatus = (
  protocol: ProtocolVersion
): 'approved' | 'draft' => {
  if (
    protocol.isApproved ||
    protocol.status?.trim().toLowerCase() === 'approved'
  ) {
    return 'approved';
  }
  return 'draft';
};

const toProtocolIntelligenceNavState = (protocol: ProtocolVersion) => ({
  ...protocol,
  status: getProtocolNavigationStatus(protocol),
});

const FilteredNoProtocolsOverlay = () => (
  <Box className="no-grid-overlay">
    <NoDataIcon aria-hidden="true" />
    <Typography
      className="typo-text headline3_size"
      data-testid="protocol-list-no-data"
    >
      {t('ai.review.protocolList.messageNoData')}
    </Typography>
  </Box>
);

const ProtocolList = () => {
  const navigate = useNavigate();
  const menuRef = useRef<ArrMenuHandle | null>(null);
  const [selectedProtocol, setSelectedProtocol] =
    useState<ProtocolVersion | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [protocolActionToast, setProtocolActionToast] =
    useState<ProtocolToastState>(defaultProtocolToastState);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({items: []});
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {field: 'lastUpdatedAt', sort: 'desc'},
  ]);
  const handleSortModelChange = useCallback((next: GridSortModel) => {
    if (!next.length) {
      return;
    }
    setSortModel(next);
  }, []);
  const [pageSize, setPageSize] = useState(
    protocolListTableProps.DEFAULT_PAGE_SIZE
  );
  const [pageNumber, setPageNumber] = useState(1);
  // Local overrides only after the user toggles; otherwise use API isActive
  const [activeOverrideByProtocolId, setActiveOverrideByProtocolId] = useState<
    Record<number, boolean>
  >({});
  const [pendingActiveProtocolIds, setPendingActiveProtocolIds] = useState<
    Record<number, boolean>
  >({});
  const [protocolListToast, setProtocolListToast] =
    useState<ProtocolToastState>(defaultProtocolToastState);

  const {mutateAsync: deleteProtocol, isPending: isDeletePending} =
    useDeleteAIProtocolIntelligence();

  const handleProtocolNameClick = (params: GridRenderCellParams) => {
    navigate(PROTOCOL_INTELLIGENCE_ROUTE, {
      state: toProtocolIntelligenceNavState(params.row as ProtocolVersion),
    });
  };

  const listRequest = useMemo(
    () =>
      buildProtocolListRequest({
        sortModel,
        filterModel,
        pageSize,
        pageNumber,
      }),
    [sortModel, filterModel, pageSize, pageNumber]
  );
  const protocolDownloadPendingRef = useRef(false);
  const [protocolDownloadToast, setProtocolDownloadToast] =
    useState<ProtocolToastState>(defaultProtocolToastState);
  const showDownloadError = () => {
    setProtocolDownloadToast({
      isOpen: true,
      message: t('ai.review.header.downloadError'),
      status: ToastType.Error,
    });
  };

  const handleProtocolDownload = async (protocol: ProtocolVersion | null) => {
    if (protocolDownloadPendingRef.current) return;

    const latestVersionId = protocol?.versionId;
    if (
      !protocol ||
      latestVersionId == null ||
      !Number.isInteger(latestVersionId) ||
      latestVersionId <= 0
    ) {
      showDownloadError();
      return;
    }

    protocolDownloadPendingRef.current = true;

    try {
      const versionDetails = await getProtocolVersionDetails({
        versionId: latestVersionId,
      });
      if (!isProtocolVersionDetails(versionDetails)) {
        throw new Error('Invalid protocol version payload');
      }
      let matchingHistoryEntry:
        | Awaited<ReturnType<typeof getProtocolVersionHistory>>[number]
        | undefined;
      try {
        const versionHistory = await getProtocolVersionHistory({
          protocolId: protocol.protocolId,
        });
        matchingHistoryEntry = versionHistory.find(
          (entry) => entry.versionId === latestVersionId
        );
      } catch {
        // Version history is supplemental metadata; the protocol content can
        // still be downloaded when this request is unavailable.
      }

      const versionDetailsModifiedAt = getNonEmptyText(
        versionDetails.fileDetails?.lastModifiedAt
      );
      const versionDetailsModifiedBy = getNonEmptyText(
        versionDetails.fileDetails?.lastModifiedBy
      );
      const historyModifiedAt = getNonEmptyText(
        matchingHistoryEntry?.modifiedAt
      );
      const historyModifiedBy = getNonEmptyText(
        matchingHistoryEntry?.modifiedBy
      );
      const createdBy = getNonEmptyText(protocol.createdBy);
      const lastModifiedAt =
        historyModifiedAt ??
        versionDetailsModifiedAt ??
        getNonEmptyText(protocol.lastUpdatedAt);
      const lastModifiedBy =
        historyModifiedBy ?? versionDetailsModifiedBy ?? createdBy;
      const versionDetailsWithListMetadata: ProtocolPdfDetails = {
        ...versionDetails,
        latestVersion: protocol.latestVersion,
        ...(lastModifiedAt != null ? {lastModifiedAt} : {}),
        ...(lastModifiedBy != null ? {lastModifiedBy} : {}),
      };
      createProtocolPdf(versionDetailsWithListMetadata);
    } catch {
      showDownloadError();
    } finally {
      protocolDownloadPendingRef.current = false;
    }
  };

  const {data, isLoading, isFetching, isError, refetch} =
    useGetAllProtocolIntelligenceList(listRequest, true);

  const {mutate: updateProtocolActiveState} = useUpdateProtocolActiveState();

  const hasProtocolListLoadError = isError;
  const rows: ProtocolVersion[] = hasProtocolListLoadError
    ? []
    : (data?.protocolVersions ?? []);
  const totalRecords = hasProtocolListLoadError
    ? 0
    : (data?.totalRecords ?? rows.length);
  const hasActiveFilters = filterModel.items.some(
    (item) => String(item.value ?? '').trim().length > 0
  );
  const showHeaderFilters =
    !hasProtocolListLoadError && (rows.length > 0 || hasActiveFilters);

  const handleRetryLoadProtocols = () => {
    refetch();
  };

  const handleProtocolListToastClose = () => {
    setProtocolListToast(defaultProtocolToastState);
  };

  const handleActiveToggle = useCallback(
    (protocolId: number, nextChecked: boolean, currentChecked: boolean) => {
      if (pendingActiveProtocolIds[protocolId]) {
        return;
      }

      setActiveOverrideByProtocolId((prev) => ({
        ...prev,
        [protocolId]: nextChecked,
      }));
      setPendingActiveProtocolIds((prev) => ({
        ...prev,
        [protocolId]: true,
      }));

      updateProtocolActiveState(
        {
          protocolId,
          isActive: nextChecked ? 1 : 0,
        },
        {
          onSuccess: () => {
            setPendingActiveProtocolIds((prev) => {
              const next = {...prev};
              delete next[protocolId];
              return next;
            });
            setProtocolListToast({
              isOpen: true,
              message: t(
                'ai.review.protocolIntelligence.isProtocolActiveUpdateSuccess'
              ),
              status: ToastType.Success,
            });
          },
          onError: (error: any) => {
            setActiveOverrideByProtocolId((prev) => ({
              ...prev,
              [protocolId]: currentChecked,
            }));
            setPendingActiveProtocolIds((prev) => {
              const next = {...prev};
              delete next[protocolId];
              return next;
            });
            setProtocolListToast({
              isOpen: true,
              message:
                error?.response?.data?.detail ??
                t(
                  'ai.review.protocolIntelligence.isProtocolActiveUpdateFailure'
                ),
              status: ToastType.Error,
            });
          },
        }
      );
    },
    [pendingActiveProtocolIds, updateProtocolActiveState]
  );

  const renderProtocolListErrorOverlay = () => (
    <Box
      className="protocol-list-error-overlay"
      data-testid="protocol-list-error-overlay"
    >
      <Typography
        className="typo-text subhead1_size text-headings protocol-list-error-title"
        data-testid="protocol-list-error-title"
      >
        {t('ai.review.protocolList.protocolApiFailureTitle')}
      </Typography>
      <Typography
        className="typo-text body2_size text-body-subtle protocol-list-error-description"
        data-testid="protocol-list-error-description"
      >
        {t('ai.review.protocolList.protocolApiFailureDescription')}
      </Typography>
      <ArrButton
        variant="primary"
        size="small"
        dataTestId="protocol-list-error-refresh-button"
        onClick={handleRetryLoadProtocols}
      >
        {t('common.refresh')}
      </ArrButton>
    </Box>
  );

  const handleMoreIconClick = (
    e: React.MouseEvent<HTMLElement>,
    protocol: ProtocolVersion
  ) => {
    setSelectedProtocol(protocol);
    menuRef.current?.open(e.currentTarget as HTMLElement);
  };

  const handleDeleteProtocol = async () => {
    if (!selectedProtocol) return;

    const protocolName = selectedProtocol.protocolName ?? '';

    try {
      await deleteProtocol({protocolId: selectedProtocol.protocolId});
      setIsDeleteModalOpen(false);
      setProtocolActionToast({
        isOpen: true,
        status: ToastType.Success,
        message: t(
          'ai.review.protocolIntelligence.deleteProtocolModal.deleteProtocolSuccess',
          {protocolName}
        ),
      });
    } catch (error: any) {
      setIsDeleteModalOpen(false);
      if (isConflictError(error)) {
        setProtocolActionToast({
          isOpen: true,
          status: ToastType.Error,
          message: t(
            'ai.review.protocolIntelligence.deleteProtocolModal.deleteProtocolInUseByJob'
          ),
        });
        return;
      }
      setProtocolActionToast({
        isOpen: true,
        status: ToastType.Error,
        message: t(
          'ai.review.protocolIntelligence.deleteProtocolModal.deleteProtocolFailed',
          {protocolName}
        ),
      });
    }
  };

  const onMenuItemClick = useCallback(
    (item: CustomMenuItem) => {
      if (!selectedProtocol) {
        menuRef.current?.close();
        return;
      }

      if (item.id === 'viewEdit') {
        navigate(PROTOCOL_INTELLIGENCE_ROUTE, {
          state: toProtocolIntelligenceNavState(selectedProtocol),
        });
      } else if (item.id === 'deleteProtocol') {
        setIsDeleteModalOpen(true);
      } else if (item.id === 'download') {
        handleProtocolDownload(selectedProtocol);
      }

      menuRef.current?.close();
    },
    [navigate, selectedProtocol]
  );

  const getColumns = (): GridColDef[] => {
    const columns: GridColDef[] = [
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 48,
        minWidth: 48,
        maxWidth: 48,
        sortable: false,
        filterable: false,
        resizable: false,
        renderCell: (params: GridRenderCellParams) => (
          <ArrIconButton
            onClick={(e) =>
              handleMoreIconClick(e, params.row as ProtocolVersion)
            }
            icon={<MoreIcon aria-hidden="true" />}
            ariaLabel={t('ai.review.protocolList.actions.openFor', {
              protocolName: (params.row as ProtocolVersion).protocolName,
            })}
            shape="none"
            testId={`protocol-more-action-btn-${params.id}`}
            className="protocol-list-icon-button"
          />
        ),
      },
    ];

    protocolColumnsData.forEach((col: IProtocolColumnsData) => {
      columns.push({
        field: col.field,
        headerName: col.headerName,
        filterable: col.filterable,
        sortable: col.sortable,
        flex: 1,
        minWidth: col.width,
        renderHeader: (params) => (
          <Typography
            className="typo-text label2_size"
            data-testid={`protocol-list-header-${params.field}`}
          >
            {params.colDef.headerName}
          </Typography>
        ),
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as ProtocolVersion;
          const rawValue = row[col.fieldDataKey as keyof ProtocolVersion];
          const displayValue = col.formatFunction
            ? col.formatFunction(rawValue, params.row)
            : (rawValue ?? '');
          const text = String(displayValue ?? '');

          if (col.field === 'protocolName' && text.trim()) {
            return (
              <ArrButton
                onClick={() => handleProtocolNameClick(params)}
                variant="text"
                size="xs"
                className="id-cell-preview"
                dataTestId={`protocol-list-button-${params.id}`}
                aria-label={t('ai.review.protocolList.actions.openProtocol', {
                  protocolName: text,
                })}
              >
                {cellText(
                  text,
                  `button-${col.field}-${params.id}`,
                  params,
                  'text-action-default'
                )}
              </ArrButton>
            );
          }

          if (col.field === 'status') {
            return cellText(
              toCapitalized(text),
              `text-${col.field}-${params.id}`,
              params,
              getStatusColorClass(text)
            );
          }

          return cellText(text, `text-${col.field}-${params.id}`, params);
        },
        renderHeaderFilter: (params) => (
          <>
            {col.filterable && showHeaderFilters && (
              <FilterTextField
                dataTestId={col.headerName}
                gridRenderHeaderFilterProps={params}
                placeholder={t('common.filter')}
                className="custom-filter"
                aria-label={t('common.filterColumn', {
                  column: col.headerName,
                })}
              />
            )}
          </>
        ),
      });
    });

    columns.push({
      field: 'isActive',
      headerName: t('ai.review.protocolList.columns.active'),
      filterable: false,
      sortable: true,
      flex: 0.4,
      minWidth: 100,
      renderHeader: () => (
        <Typography
          className="typo-text label2_size"
          data-testid="protocol-list-header-isActive"
        >
          {t('ai.review.protocolList.columns.active')}
        </Typography>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const protocolId = params.row.protocolId as number;
        const checked =
          activeOverrideByProtocolId[protocolId] ??
          isProtocolActive(params.row.isActive);
        const isTogglePending = Boolean(pendingActiveProtocolIds[protocolId]);
        return (
          <ArrToggle
            toggleSize="medium"
            checked={checked}
            disabled={isTogglePending}
            onChange={(event, nextChecked) => {
              handleActiveToggle(protocolId, nextChecked, checked);
              (event.target as HTMLElement | null)?.blur?.();
            }}
            className="protocol-list-active-toggle"
            dataTestId={`protocol-active-toggle-${protocolId}`}
            ariaLabel={t('ai.review.protocolList.actions.toggleActiveFor', {
              protocolName: params.row.protocolName,
            })}
          />
        );
      },
    });

    return columns;
  };

  const isGridLoading = isLoading || isFetching;

  return (
    <Box className="protocol-list" data-testid="protocol-list">
      <ArrDataGridPro
        rowHeight={protocolListTableProps.ROW_HEIGHT}
        data-testid="ai-protocol-list-data-grid"
        columns={getColumns()}
        columnHeaderHeight={48}
        rows={rows}
        rowCount={totalRecords}
        noRowsOverlay={
          hasProtocolListLoadError
            ? renderProtocolListErrorOverlay
            : hasActiveFilters
              ? FilteredNoProtocolsOverlay
              : (data?.protocolVersions?.length ?? 0) === 0
                ? () => <NoProtocols />
                : undefined
        }
        getRowId={(row: ProtocolVersion) => row.protocolId}
        showPerPageOptions
        itemsPerPage={pageSize}
        setItemsPerPage={setPageSize}
        currentPage={pageNumber}
        onPageChange={setPageNumber}
        pageSizeOptions={protocolListTableProps.PAGE_SIZE_OPTIONS}
        pagination
        disableColumnResize={false}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        sortingOrder={['asc', 'desc']}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        headerFilters={showHeaderFilters}
        headerFilterHeight={protocolListTableProps.FILTER_HEADER_HEIGHT}
        disableColumnReorder
        showCellVerticalBorder
        disableVirtualization
        entityType={t('ai.review.protocolList.protocols')}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        disableColumnFilter
        loading={isGridLoading}
        loadingOverlay={() => <ArrLoadingSpinner />}
        alwaysShowPagination
        hideFooter={rows.length === 0 && !hasActiveFilters}
      />
      <ArrMenu
        id="protocol-action-menu"
        ref={menuRef}
        items={protocolActionsMoreMenu}
        onItemClick={onMenuItemClick}
        direction="left"
        className="protocol-action-menu"
        autoFocusMenu={true}
        disableAutoFocusItem={false}
      />
      {protocolListToast.isOpen && (
        <ArrToastNotification
          message={protocolListToast.message}
          onClose={handleProtocolListToastClose}
          status={[protocolListToast.status]}
          data-testid="protocol-list-toast"
          aria-label={protocolListToast.message}
        />
      )}
      {isDeleteModalOpen && (
        <ArrModal
          title={t('ai.review.protocolIntelligence.deleteProtocolModal.title', {
            protocolName: selectedProtocol?.protocolName ?? '',
          })}
          showModal={isDeleteModalOpen}
          onClose={() => {}}
          showClose={false}
          dataTestId="delete-protocol-modal"
          buttons={[
            {
              id: t('common.cancel'),
              label: t('common.cancel'),
              onClick: () => setIsDeleteModalOpen(false),
              variant: 'secondary',
              dataTestId: 'dp-cancel-delete',
            },
            {
              id: t('common.delete'),
              label: t('common.delete'),
              onClick: handleDeleteProtocol,
              variant: 'danger',
              dataTestId: 'dp-confirm-delete',
              loading: isDeletePending,
              disabled: isDeletePending,
            },
          ]}
          className="delete-protocol-modal"
        >
          <Box className="delete-protocol-modal-description">
            <Typography
              className="typo-text body2_Size text-body-strong"
              data-testid="protocol-list-delete-p1"
            >
              {t('ai.review.protocolIntelligence.deleteProtocolModal.p1')}
            </Typography>
            <Typography
              className="typo-text body2_Size text-body-strong"
              data-testid="protocol-list-delete-p2"
            >
              {t('ai.review.protocolIntelligence.deleteProtocolModal.p2')}
            </Typography>
          </Box>
        </ArrModal>
      )}
      {protocolActionToast.isOpen && (
        <ArrToastNotification
          message={protocolActionToast.message}
          status={[protocolActionToast.status]}
          onClose={() => setProtocolActionToast(defaultProtocolToastState)}
        />
      )}
      {protocolDownloadToast.isOpen && (
        <ArrToastNotification
          message={protocolDownloadToast.message}
          status={[protocolDownloadToast.status]}
          onClose={() => setProtocolDownloadToast(defaultProtocolToastState)}
          data-testid="protocol-download-toast"
          aria-label={protocolDownloadToast.message}
        />
      )}
    </Box>
  );
};

export default ProtocolList;
